import {Lang} from 'yougile-lang'
import {FilenameUtil, logger, Util} from 'yougile-util'
import {Backend} from '../../common/backend'
import {FilePreview} from '../../common/file-preview'
import {MessageBox} from '../components/message-box'
import {ExtFile} from '../types/extfile'
import {Size} from '../types/geometry'

const mimeTypesExtensions: {[mime: string]: string} = {
  'text/plain': 'txt',
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'application/pdf': 'pdf',
  'application/zip': 'zip',
  'video/mp4': 'mp4',
  'video/mpeg': 'mpeg',
  'audio/mpeg': 'mp3'
}

export class ClientUtilities {
  private static setLanguageFromBrowser(): void {
    if (
      'userLanguage' in window.navigator &&
      (window.navigator as any).userLanguage.indexOf('ru') === 0
    ) {
      Lang.setLang('ru')
    } else if (
      'language' in window.navigator &&
      window.navigator.language.startsWith('ru')
    ) {
      Lang.setLang('ru')
    } else if (
      'languages' in window.navigator &&
      window.navigator.languages.find(l => l.startsWith('ru'))
    ) {
      Lang.setLang('ru')
    } else {
      Lang.setLang('en')
    }
  }

  static setLangFromUrlOrBrowser(): void {
    const host = location.host
    const search = location.search
    if (search.includes('lang=ru')) {
      Lang.setLang('ru')
    } else if (search.includes('lang=en')) {
      Lang.setLang('en')
    } else if (host.includes('ru.')) {
      Lang.setLang('ru')
    } else if (host.startsWith('en.') || host.includes('-en.')) {
      Lang.setLang('en')
    } else {
      ClientUtilities.setLanguageFromBrowser()
    }
  }

  static setDefaultName(file: ExtFile): void {
    if (file.name) {
      return
    }
    const ext =
      file.type && file.type in mimeTypesExtensions
        ? '.' + mimeTypesExtensions[file.type]
        : ''
    ;(file as any).name = 'file' + ext
  }

  static saveFile(
    source: {file: ExtFile; size?: Size},
    callback?: (url?: string) => void,
    onProgress?: (percent: number) => void,
    options?: {mimeType: string; quality: number; raw: boolean}
  ): Promise<string | undefined> {
    const {file} = source
    if (file.size && file.size > Backend.uploadFileLimit * 1024 * 1024) {
      MessageBox.warn(Lang.msg.common.fileSizeWarn(Backend.uploadFileLimit))
      callback?.()
      return Promise.resolve(undefined)
    }

    ClientUtilities.setDefaultName(file)
    const data = new FormData()
    data.append('file', file as Blob)

    return new Promise(ok => {
      const cb = (url: string | undefined): void => {
        callback?.(url)
        ok(url)
      }
      this.createPreviewAndPostToBackend({
        source,
        data,
        callback: cb,
        onProgress,
        options
      })
    })
  }

  static async createPreviewForServerImage(
    url: string,
    previewMaxSize: Size,
    callback: (url?: string) => void,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    const file = await this.getFileFromUrl(url)
    const data = new FormData()
    this.createPreviewAndPostToBackend({
      source: {file},
      data,
      callback,
      onProgress,
      previewMaxSize
    })
  }

  static async downloadFile(url: string): Promise<void> {
    const file = await this.getFileFromUrl(url)
    const objectUrl = window.URL.createObjectURL(file)
    const a = document.createElement('a')
    a.style.display = 'none'
    a.href = objectUrl
    a.download = FilenameUtil.getFilename(url)
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(objectUrl)
    document.body.removeChild(a)
  }

  private static async getFileFromUrl(
    url: string,
    defaultType = 'image/jpeg'
  ): Promise<File> {
    url = Util.isDesktop() && url.startsWith('/') ? Backend.toMainUrl(url) : url
    const response = await fetch(url)
    const data = await response.blob()
    const name = FilenameUtil.getFilename(url)
    return new File([data], name, {
      type: data.type || defaultType
    })
  }

  private static createPreviewAndPostToBackend(props: {
    source: {
      file: ExtFile
      size?: Size
    }
    data: FormData
    callback: (url?: string) => void
    onProgress?: (percent: number) => void
    previewMaxSize?: Size
    options?: {mimeType: string; quality: number; raw: boolean}
  }) {
    const {source, data, callback, onProgress, previewMaxSize} = props

    FilePreview.getFilePreview(
      source,
      (urlParams: string, previews: File[]) => {
        previews.forEach((prev: File, ix: number) => {
          data.append('preview' + ix, prev, prev.name)
        })
        Backend.postMultipart({
          url: '/data/file',
          data,
          callback: (json: {result: string; urls: string[]}) => {
            if (!json || json.result !== 'ok') {
              logger.error('Failed to save file')
              callback()
            } else {
              if (json.urls.length === 1) {
                callback(json.urls[0])
              } else {
                const sorted = [...json.urls].sort(
                  (a, b) => a.length - b.length
                )
                const originalFileUrl = sorted[0] // original is the shortest
                callback(originalFileUrl + urlParams)
              }
            }
          },
          onProgress
        })
      },
      previewMaxSize,
      props.options
    )
  }

  static saveExtension(source: {file: ExtFile; size?: Size}): void {
    const {file} = source

    ClientUtilities.setDefaultName(file)

    const data = new FormData()
    data.append('file', file as Blob)

    this.postExtensionToBackend({
      source,
      data
    })
  }

  private static postExtensionToBackend(props: {
    source: {
      file: ExtFile
      size?: Size
    }
    data: FormData
  }): void {
    const {data} = props

    Backend.postMultipart({
      url: '/data/upload-extension',
      data,
      callback: (json: {result: string}) => {
        if (!json || json.result !== 'ok') {
          logger.error('Failed to save file', json)
          MessageBox.danger(Lang.msg.extensions.errorOnInstall)
        } else {
          MessageBox.success(Lang.msg.extensions.successOnInstall)
        }
      }
    })
  }
}
